import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, saved_view } from "@listto/powersync";
import { nowISO } from "../utils/date-helpers";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { generateId } from "@fleet/utils";
import { type SavedViewFilters, serializeSavedViewFilters } from "./model";

/**
 * Creates a user's saved view (synchronizes via PowerSync).
 */
export async function createSavedViewMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
  filters: SavedViewFilters,
  opts?: { icon?: string; color?: string; sortOrder?: number },
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(saved_view).values({
    id,
    user_id: userId,
    name: name.trim(),
    icon: opts?.icon ?? null,
    color: opts?.color ?? null,
    filters: serializeSavedViewFilters(filters),
    sort_order: opts?.sortOrder ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Updates the name/filters/order of a saved view.
 *
 * It uses `patchRowVerified` (writes → rereads → compares → retries → throws):
 * a raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server. See
 * `../powersync/verified-write.ts`. **It can throw.**
 */
export async function updateSavedViewMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: {
    name?: string;
    filters?: SavedViewFilters;
    icon?: string | null;
    color?: string | null;
    sortOrder?: number;
  },
  options?: VerifiedWriteOptions,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (updates.name !== undefined) {
    patch.name = updates.name.trim();
  }
  if (updates.filters !== undefined) {
    patch.filters = serializeSavedViewFilters(updates.filters);
  }
  if (updates.icon !== undefined) {
    patch.icon = updates.icon;
  }
  if (updates.color !== undefined) {
    patch.color = updates.color;
  }
  if (updates.sortOrder !== undefined) {
    patch.sort_order = updates.sortOrder;
  }
  await patchRowVerified(db, saved_view, saved_view.id, id, patch, options);
}

/**
 * Deletes a saved view (hard: it is lightweight metadata with no trash).
 */
export async function deleteSavedViewMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(saved_view).where(eq(saved_view.id, id));
}
